import { createUrlRelativeToEnvironment } from "client_legacy/ndaw/navigator";
import { DocumentStoreType } from "../Documents/common";
import { ListLocationModel } from "../Documents/SharePoint/common";
import { UserIdentityModel } from "../UserProfile/service";
import { HubDataSource } from "./hub";
import { never } from "client_legacy/ndaw/errors";

// Focal point are percentage-based (range from 0.0 to 1.0)
export type ImageFocalPointModel = {
	left: number;
	top: number;
};

export interface HubItemCategoryIdentifierModel {
	name: string;
	categoryId: number | null;
	canCreateNewItem: boolean;
	newItemsCount: number;
}

export enum HubTileLayoutKind {
	List = 0,
	Carousel = 1,
	Grid = 2,
	Spotlight = 3,
	Feed = 4
}

export enum HubItemRowLayoutKind {
	OneColumn = 1,
	TwoColumn = 2,
	ThreeColumn = 3,
	FourColumn = 4,
	TwoColumnBigLeft = 5,
	TwoColumnBigRight = 6
};

export enum HubItemElementKind {
	Subtitle = 1,
	Paragraph = 2,
	Image = 3,
	Embed = 4,
	HorizontalLine = 5,
	Video = 6,
	FrequentlyAskedQuestion = 7
}

export enum HubItemMediaKind {
	None = 0,
	Image = 1,
	Embed = 2,
	Video = 3
}

export type NoneHubMediaPreviewViewModel = {
	mediaKind: HubItemMediaKind.None;
};

export type ImageHubMediaPreviewViewModel = {
	mediaKind: HubItemMediaKind.Image;
	imageId: string;
	imageName: string;
	focalPoint: ImageFocalPointModel;
};

export type VideoHubMediaPreviewViewModel = {
	mediaKind: HubItemMediaKind.Video;
	videoUrl: string;
};

export type EmbedHubMediaPreviewViewModel = {
	mediaKind: HubItemMediaKind.Embed;
};

export type HubItemMediaPreviewViewModel =
	| NoneHubMediaPreviewViewModel
	| ImageHubMediaPreviewViewModel
	| VideoHubMediaPreviewViewModel
	| EmbedHubMediaPreviewViewModel;

export type UserMentionViewModel = {
	from: number;
	to: number;
	user: UserIdentityModel;
};

export type HubCommentMediaPreviewModel = {
	mediaKind: HubCommentMediaKind.Image;
	imageId: string;
	imageName: string;
};

export type HubCommentModel = {
	id: number;
	message: string;
	mentions: readonly UserMentionViewModel[];
	media: HubCommentMediaPreviewModel | null;
	publicationTime: Date | string;
	lastModificationTime: Date | string;
	userInfo: UserIdentityModel;
	currentUserCanEdit: boolean;
	currentUserCanDelete: boolean;
	statistics: HubCommentStatisticsModel;
	parentCommentId: number | null;
};

export type HubCommentStatisticsModel = {
	commentId: number;
	reactionsCount: number;
	reactionTypes: ReactionCounterModel[];
	currentUserReaction: ReactionKind;
};

export type UserMentionSaveModel = {
	from: number;
	to: number;
	userId: number;
};

export enum HubCommentMediaKind {
	Image = 1
};

export type HubCommentMediaSaveModel = {
	mediaKind: HubCommentMediaKind.Image;
	image: OptionalModel<Base64FileUriUploadModel>;
};

export type HubCommentSaveModel = {
	message: string;
	mentions: readonly UserMentionSaveModel[];
	media: OptionalModel<HubCommentMediaSaveModel | null>;
};

export type HubLinkedDocumentViewModel = {
	name: string;
	extension: string;
} & (
	| SharePointLinkedDocumentModel
	| WebDavLinkedDocumentModel
);

export type SharePointLinkedDocumentModel = {
	documentStoreType: DocumentStoreType.SharePoint;
	itemId: number;
	location: ListLocationModel;
};

export type WebDavLinkedDocumentModel = {
	documentStoreType: DocumentStoreType.WebDav;
	serverId: number;
	fileId: string;
};

export type FrequentlyAskedQuestionItemViewModel = {
	question: string;
	answer: string;
};

export enum HubItemCategoryNodeKind {
	Category = 1,
	Group = 2
};

export enum ReactionKind {
	NoReaction = 0,
	Like = 1,
	Love = 2,
	Sad = 3,
	Celebrate = 4,
	Laugh = 5,
	Thank = 6
};

export type ReactionCounterModel = {
	kind: ReactionKind;
	count: number;
};

export type ReactionUserModel = {
	user: UserIdentityModel;
	reactionKind: ReactionKind;
};

export enum HubItemViewerListItemKind {
	User = 0,
	DeletedUsers = 1
};

export function getHubImageUrl(source: HubDataSource, imageId: string): string {
	switch (source) {
		case HubDataSource.Announcement:
			return createUrlRelativeToEnvironment(`/Hub/Announcements/Image/?id=${encodeURIComponent(imageId)}`);
		case HubDataSource.KnowledgeItem:
			return createUrlRelativeToEnvironment(`/Hub/KnowledgeItems/Image/?id=${encodeURIComponent(imageId)}`);
		case HubDataSource.Event:
			return createUrlRelativeToEnvironment(`/Hub/Events/Image/?id=${encodeURIComponent(imageId)}`);
		case HubDataSource.Community:
			return createUrlRelativeToEnvironment(`/Hub/Communities/Image/?id=${encodeURIComponent(imageId)}`);
		default:
			never(source);
	}
}

type UserHubItemViewerListItemModel = {
	kind: HubItemViewerListItemKind.User;
	user: UserIdentityModel;
};

type DeletedUsersHubItemViewerListItemModel = {
	kind: HubItemViewerListItemKind.DeletedUsers;
	count: number;
};

export type HubItemViewerListItemModel =
	| UserHubItemViewerListItemModel
	| DeletedUsersHubItemViewerListItemModel;

export type HubItemImageRestrictionsModel = {
	maxImageSize: number;
	supportedMimeTypes: readonly string[];
	supportedFileExtensions: readonly string[];
};

export type HubCommentPagingInformationModel = {
	id: number;
	publicationTime: Date | string;
};
